import React, {useEffect, useState} from "react";
import {comparators, comparatorsField, MS_DAY, utcMsToDate} from "../tools/CommonJS";
import {useStateEx} from "../tools/CommonReact";
import {appCtx, c_defaultCacheMins, isAWS, isOnPrem, queryOrErr, useFetchData, useFetchDataCb, useFetchDataSingleton} from "./AppContext";
import {Button, Checkbox, TextInput} from "../tools/Inputs";
import {AccordionDiv, DashCard, ListItemValue} from "./CommonUIComponenets";
import {classNameScrollbar, LoadingAnim} from "../tools/CommonHtml";
import {Dialog} from "../tools/UIDialog";
import {mockData} from "./MockData.js";
import licenseGuide from "./license_installation_guide.html?raw";
import {ToggleDivByHeader} from "../tools/UIAnimateToggleDiv";
import {useUser} from "./UseAppData";
import {checkRoleAdmin} from "./CommonUIData";

const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

const licenseGuideEx = licenseGuide
    .replace(/(<h1) /g, `$1 class="text-lg"`)
    .replace(/(<h2) /g, `$1 class="text-base border-b border-badge"`)
    .replace(/<p>/g, `<p class="pl-2 font-sans">`)
    .replace(/<pre>/g, `<pre class="overflow-auto bg-bg3 p-4 rounded-lg ${classNameScrollbar}">`);

export function useLicense() {
    function onData({data: licenses, ...info}) {
        const now = Date.now();
        licenses.sort(comparatorsField(comparators.dateSig, "startDate"));
        const iCur = licenses.findIndex(l => new Date(l.startDate) <= now && new Date(l.endDate) > now);
        return {
            licenses,
            info,
            curLic: licenses[iCur],
            nextLic: licenses[iCur+1],
            expDate: licenses.last && new Date(licenses.last.endDate),
            cacheTime: Date.now()
        };
    }

    const {rsp, refresh} = useFetchDataSingleton({api: "/api/v2/license_get", onData, alt: mockData.licenseGet});

    useEffect(() => {
        if (rsp?.cacheTime < Date.now() - c_defaultCacheMins *60*1000)      // if data is already loaded, but older than 10 minutes, refresh.
            refresh();
    }, []);

    const {licenses, info, curLic, nextLic, expDate} = rsp ?? {};
    return {licenses, info, curLic, nextLic, expDate, refreshLicense: _ => refresh()};
}

export const checkModuleLicense = (lic, module) =>
    !module || Object.values(lic?.devices ?? {}).some(d => d.modules.includes(module));


export function CheckLicense({children}) {
    const jsxEula = useEulaCheck();
    const {licenses, curLic} = useLicense();
    return jsxEula ?? (!licenses || curLic? children : <LicenseStatus asMain/>);
}

export default function LicenseStatus({asMain}) {
    // const [newOrg, setNewOrg] = useState("");
    const [licIndex, setLicIndex] = useState(0);
    const {licenses, curLic} = useLicense();
    // const {fetchData} = useFetchDataCb();
    /*
    function onGenId() {
        appCtx.setDlg({Elm: GenerateIdDialog, company: info?.company, setNewOrg});
    }

    function uploadLicense() {
        selectFile(file =>
            readFile(file, licenseKey => {
                if (licenseKey.length < 300 || licenseKey.length > 2000 || !base64regex.test(licenseKey))
                    return msgBox("File contents are not a valid base64 key. Please verify that the file contains a single line of alphanumeric and punctuation characters.");
                fetchData({api: "/api/v2/license_upload", params: {licenseKey}, onData: refreshLicense,
                    setMsg: msg => msgBox(<>{msg}<div>Please verify that the correct license file with extension '.key' was selected.</div></>)});
            })
        );
    }
    */
    return !licenses? null :
        <div className={`w-full flex flex-col gap-8 ${asMain && "p-8"}`} data-test="licenses-container">
            <DashCard  data-test="licenses-data-container" className={`p-card flex flex-col gap-4 ${asMain? "w-[45rem]" : "w-full"}`}>
                {asMain && <div className="text-xl font-semibold">License Status</div>}
                {!curLic && <div className="text-sec text-base">
                    {!!licenses.length && "License has expired! "}{!isAWS && "No valid license found. Please follow the steps below to setup a license:"}
                </div>}
                {licenses.map((lic, idx) =>
                    <AccordionDiv
                        className="bg-bg3 px-4 py-2" handle
                        key={lic.startDate} isExpanded={licIndex === idx} onSetExpanded={() => setLicIndex(i => i === idx? -1 : idx)} arrow
                        summary={
                            <div className="flex gap-6" data-test={`license-summary-${idx}`}>
                                <ListItemValue h="Start" val={lic.startDate && utcMsToDate(new Date(lic.startDate).getTime() || 0)}/>
                                <ListItemValue h="End" val={lic.endDate && utcMsToDate((new Date(lic.endDate).getTime() || 0) - MS_DAY)}/>
                            </div>
                        }>
                        {Object.entries(lic.devices).map(([dev, devLic]) =>
                            <div key={dev} className="pl-4" data-test={`device-${dev}`}>
                                <div>{dev || "All"}:</div>
                                <div className="pl-4">Modules: {devLic.modules.join(", ")}</div>
                                <div className="pl-4">Device Limit: {devLic.devices.toLocaleString()}  (utilizing: {devLic.activeDevices.toLocaleString()})</div>
                            </div>
                        )}
                    </AccordionDiv>
                )}
            </DashCard>
            {isOnPrem && <DashCard className={`p-card flex flex-col gap-4`}>
                <ToggleDivByHeader
                    classNameHeader="text-desc text-lg font-semibold"
                    classNameContent={`whitespace-pre-wrap`}
                    dispHeader={"How To Install a License..."}>
                    <div className="p-2 pt-4" dangerouslySetInnerHTML={{__html: licenseGuideEx}}/>
                </ToggleDivByHeader>
            </DashCard>}
        </div>;
}

function usePublicFileLoader({fileName}) {
    const [text, setText] = useState("");

    useEffect(() => {
        fetch(`${import.meta.env.BASE_URL || ""}${fileName}`)
            .then(res => res.text())
            .then(setText)
            .catch(err => {
                console.error("Could not load text file", err);
                setText("Failed to load file");
            });
    }, []);

    return text;
}

const eulaVer = "1.0.1";

function useEulaCheck() {
    const {user} = useUser(), isAdmin = checkRoleAdmin(user?.perms?.[0]);
    const {rsp, refresh} = useFetchData({api: "/api/v2/eula_get", alt: {isSigned: true, version: eulaVer}});

    const showEula = rsp && (!rsp.isSigned || (rsp.version && eulaVer !== rsp.version));

    useEffect(() => {
        if (isAdmin && showEula)
            appCtx.setDlg({Elm: DlgEulaAccept, refresh});
    }, [showEula, isAdmin]);

    return !rsp? <LoadingAnim classNameSize="mt-10 w-10 h-10 self-center"/> :
        !showEula? null :       // continue to app
            !isAdmin? <div className="w-full text-center mt-10 text-lg">EULA has not been accepted. Please ask your Corporate's Remedio admin to login and Accept the EULA.</div> :
                " ";            // show nothing - wait for EULA dialog!
}


const rxFullName = /\p{L}+\s+\p{L}+/u;

function DlgEulaAccept({onClose, refresh}) {
    const text = usePublicFileLoader({fileName: "EULA.txt"});
    const {state: st, setState, onChange} = useStateEx();
    onClose.onClose = () => false;                      // prevent closing the dialog by outside click.

    function onAccept() {
        queryOrErr({
            api: "/api/v2/eula_sign",
            params: {fullName: st.fullName, version: eulaVer},
            onLoading: isLoading => setState({isLoading}),
            alt: {},
            rsp: () => {
                refresh();
                delete onClose.onClose;     // allow dialog close.
                onClose();
            }
        });
    }

    const checkFullName = (v = st.fullName) => !rxFullName.test(v) && "Please enter your full name.";

    return (
        <Dialog
            className="dlg- w-[95vw] tall:max-h-"
            title="End User License Agreement (EULA)"
            mainProps={{onScroll: e => e.target.scrollTop > e.target.scrollHeight - e.target.clientHeight * 1.2 && onChange("read", true)}}
            main={<div className="whitespace-pre-wrap text-title/80">{text}</div>}
            bottom={<div className="flex flex-wrap items-center gap-4 w-full">
                <Checkbox label="I have read and accept the EULA." field="check" isChecked={st.check} onChange={onChange} disabled={!st.read}/>
                <div className={`ml-8 ${!st.check && "text-title/30"}`}>Full Name: </div>
                <TextInput
                    classNameWrapper="w-60" field="fullName" value={st.fullName || ""} onChange={onChange} validate={v => v && checkFullName(v)}
                    disabled={!st.check}/>
                <div className="grow"/>
                {st.isLoading && <LoadingAnim classNameSize="w-7 h-7"/>}
            </div>}
            buttons={
                <Button
                    h="Accept" className="px-10 py-2 text-base text-prm-contrast bg-blue-500" onClick={onAccept}
                    disabled={(!st.check && "Please read all the complete EULA, then click the checkbox and enter your full name.") ||
                            checkFullName()}/>
            }/>
    );
}


/*
const GenIdDialog_fields = [
    {f: "org", h: "Organization", p: "(name)", t: "text"},
    {f: "person", h: "Contact Person", p: "(name)", t: "text"},
    {f: "email", h: "Email", p: "(email address)", t: "email"},
];

function GenerateIdDialog({onClose, company, setNewOrg}) {
    const {inp, onChange} = useInputChange({...company});
    const {fetchData} = useFetchDataCb();

    function checkField(v) {
        if (!v)
            return `Input required.`;
    }

    function checkSave() {
        const {org, person, email} = inp;
        return [org, person, email].findPred(checkField);
    }

    function onGenId(e) {
        const {org} = inp;

        fetchData({
            api: "/api/v2/license_gen-id",
            params: inp,
            onData: r => {
                setNewOrg(org);
                downloadUri({filename: `${org && org.replace(/ /g, "_") || ""}_license.id`, content: r.licenseId});
                onClose();
            }
        });
    }

    return (
        <Dialog
            {...{onClose}} className="dlg-x xl:min-w-[35rem]"
            title="Generate License ID"
            main={<div className="flex flex-col gap-3.5 text-sm">
                {GenIdDialog_fields.map(e =>
                    <FormLabel key={e.f} label={e.h} classNameCol1="w-36" classNameCol2="w-full">
                        <TextInput field={e.f} type={e.t} value={inp[e.f]} {...{onChange}} validate={checkField}/>
                    </FormLabel>)}
            </div>}
            buttons={<>
                <Button h="Cancel" className="px-6 py-2 text-base text-prm-main" onClick={onClose}/>
                <Button h="Generate" className="px-10 py-2 text-base text-prm-contrast bg-blue-500" onClick={onGenId} disabled={checkSave()}/>
            </>}
        />
    );
}


function MailtoGytpolAnchor({org}) {
    return (
        <a className="text-orange-600 italic ml-1"
           href={`mailto:license@gytpol.com?subject=${escape(`License request for ${org || ""}`)}&body=${escape("Hi. Attaching our license.id...")}`}
           target="_blank"
        >
            license@gytpol.com
        </a>
    );
}
*/
